import { createContext, useContext, useReducer, useEffect, useMemo, useCallback, useRef } from 'react';
import { AuthProvider, useAuth } from '../firebase/useAuth';
import { getAllPhotos, upsertPhoto, deletePhotoLocal, getAllHunts, upsertHunt, getMeta, setMeta } from '../lib/db';
import { useSync } from '../hooks/useSync';
import { useShare } from '../hooks/useShare';
import { domainForHunt, implicitTitle } from '../lib/huntDomain';

// ── State shape ──────────────────────────────────────────────────────────────

const initialState = {
  photos:       [],
  hunts:        [],           // Hunt[]
  activeHuntId: null,
  huntsSettled: false,        // true once local hunt list is known to reflect reality
  activeView:   'grid',       // 'grid' | 'location' | 'locations' | 'hunt'
  activeLocation: null,       // string | null
  activeFilter: 'all',        // 'all' | 'missing' | 'found'
  openModal:    null,         // null | 'detail' | 'addPhoto' | 'import' | 'share' | 'newHunt'
  modalData:    null,
  installPromptEvent: null,
  shareTokens:  {},           // { [huntId]: token }
  loading:      true,
};

// ── Reducer ──────────────────────────────────────────────────────────────────

function reducer(state, action) {
  switch (action.type) {
    case 'SET_PHOTOS':
      return { ...state, photos: action.photos, loading: false };

    case 'UPSERT_PHOTO': {
      const idx = state.photos.findIndex(p => p.id === action.photo.id);
      const photos = idx >= 0
        ? state.photos.map(p => p.id === action.photo.id ? action.photo : p)
        : [...state.photos, action.photo];
      return { ...state, photos };
    }

    case 'DELETE_PHOTO':
      return { ...state, photos: state.photos.filter(p => p.id !== action.id) };

    case 'SET_HUNTS':
      return { ...state, hunts: action.hunts };

    case 'UPSERT_HUNT': {
      const idx = state.hunts.findIndex(h => h.id === action.hunt.id);
      const hunts = idx >= 0
        ? state.hunts.map(h => h.id === action.hunt.id ? action.hunt : h)
        : [...state.hunts, action.hunt];
      return { ...state, hunts };
    }

    case 'DELETE_HUNT': {
      const hunts = state.hunts.filter(h => h.id !== action.id);
      const activeHuntId = state.activeHuntId === action.id
        ? (hunts[0]?.id ?? null)
        : state.activeHuntId;
      return { ...state, hunts, activeHuntId };
    }

    case 'SET_HUNTS_SETTLED':
      return { ...state, huntsSettled: true };

    case 'SET_ACTIVE_HUNT': {
      const hunt = state.hunts.find(h => h.id === action.huntId);
      // The "still looking for" list only makes sense for a fixed-domain
      // hunt — bounce out of that view if we're switching to a Custom hunt.
      const activeView = (hunt?.type === 'custom' && state.activeView === 'hunt')
        ? 'grid'
        : state.activeView;
      return { ...state, activeHuntId: action.huntId, activeView };
    }

    case 'SET_VIEW':
      return { ...state, activeView: action.view, activeLocation: action.location ?? null };

    case 'SET_FILTER':
      return { ...state, activeFilter: action.filter };

    case 'OPEN_MODAL':
      return { ...state, openModal: action.modal, modalData: action.data ?? null };

    case 'CLOSE_MODAL':
      return { ...state, openModal: null, modalData: null };

    case 'SET_INSTALL_PROMPT':
      return { ...state, installPromptEvent: action.event };

    case 'SET_SHARE_TOKENS':
      return { ...state, shareTokens: action.shareTokens };

    case 'SET_SHARE_TOKEN':
      return { ...state, shareTokens: { ...state.shareTokens, [action.huntId]: action.token } };

    default:
      return state;
  }
}

// ── Context ──────────────────────────────────────────────────────────────────

const AppContext = createContext(null);

function AppInner({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user, isOnline, signInWithGoogle, signOut } = useAuth();
  const bootstrappedRef = useRef(false);

  // Load from IDB on mount.
  useEffect(() => {
    Promise.all([getAllPhotos(), getAllHunts(), getMeta('activeHuntId'), getMeta('shareTokens')])
      .then(([photos, hunts, activeHuntId, shareTokens]) => {
        dispatch({ type: 'SET_HUNTS', hunts });
        if (shareTokens) dispatch({ type: 'SET_SHARE_TOKENS', shareTokens });
        const resolvedActiveId = (activeHuntId && hunts.some(h => h.id === activeHuntId))
          ? activeHuntId
          : (hunts[0]?.id ?? null);
        if (resolvedActiveId) dispatch({ type: 'SET_ACTIVE_HUNT', huntId: resolvedActiveId });
        dispatch({ type: 'SET_PHOTOS', photos });
      });
  }, []);

  // Firestore sync (photos + hunts). onSettled fires once we know the local
  // hunt list reflects reality (signed out, offline, or the first cloud
  // hunts snapshot has processed) — see huntsSettled bootstrap effect below.
  const handleSynced = useCallback(() => dispatch({ type: 'SET_HUNTS_SETTLED' }), []);
  useSync(user, isOnline, dispatch, handleSynced);

  // Share tokens
  useShare(user ?? null, dispatch);

  // Bootstrap a default "1-100" hunt for a genuinely new account — only once
  // we're sure there isn't cloud data still in flight that would otherwise
  // race this and produce a spurious duplicate hunt.
  useEffect(() => {
    if (!state.huntsSettled || state.hunts.length > 0 || bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    const now = new Date().toISOString();
    const hunt = {
      id: crypto.randomUUID(),
      type: 'numbers',
      title: implicitTitle('numbers'),
      huntList: [],
      createdAt: now,
      updatedAt: now,
    };

    (async () => {
      await upsertHunt(hunt);
      dispatch({ type: 'UPSERT_HUNT', hunt });
      dispatch({ type: 'SET_ACTIVE_HUNT', huntId: hunt.id });

      if (isOnline && user) {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { db: firestore } = await import('../firebase/firebaseConfig');
          await setDoc(doc(firestore, 'users', user.uid, 'hunts', hunt.id), hunt);
        } catch (err) {
          const { enqueueOp } = await import('../lib/db');
          await enqueueOp('setHunt', { userId: user.uid, hunt });
        }
      } else if (user) {
        const { enqueueOp } = await import('../lib/db');
        await enqueueOp('setHunt', { userId: user.uid, hunt });
      }
    })();
  }, [state.huntsSettled, state.hunts.length, user, isOnline]);

  // Persist the active hunt selection so it's remembered across reloads.
  useEffect(() => {
    if (state.activeHuntId) setMeta('activeHuntId', state.activeHuntId);
  }, [state.activeHuntId]);

  // Capture beforeinstallprompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      dispatch({ type: 'SET_INSTALL_PROMPT', event: e });
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Status/nav bar safe-area insets pushed from MainActivity.kt (Android is
  // edge-to-edge on targetSdk 36 — content draws under the system bars unless
  // padded away). A native View's own padding wouldn't reflow this page's CSS
  // layout, so the padding lives here instead, via CSS custom properties.
  useEffect(() => {
    window.setSafeAreaInsets = (top, bottom) => {
      document.documentElement.style.setProperty('--safe-top', `${top}px`);
      document.documentElement.style.setProperty('--safe-bottom', `${bottom}px`);
    };
    return () => { delete window.setSafeAreaInsets; };
  }, []);

  // Android hardware/gesture back button (see android/.../MainActivity.kt).
  // Native tracks a "screen" string via AndroidBridge.setScreen() and only exits
  // the app when it's 'grid'; otherwise it calls window.handleBack(), which we
  // point at a modal-close-or-return-to-grid step.
  useEffect(() => {
    if (!window.AndroidBridge?.setScreen) return;
    window.AndroidBridge.setScreen(state.openModal ? 'modal' : state.activeView);
  }, [state.openModal, state.activeView]);

  useEffect(() => {
    if (!window.AndroidBridge) return;
    window.handleBack = () => {
      if (state.openModal) dispatch({ type: 'CLOSE_MODAL' });
      else dispatch({ type: 'SET_VIEW', view: 'grid' });
    };
    return () => { delete window.handleBack; };
  }, [state.openModal]);

  // ── Derived state (memoized) ────────────────────────────────────────────────

  const activeHunt = useMemo(
    () => state.hunts.find(h => h.id === state.activeHuntId) ?? null,
    [state.hunts, state.activeHuntId]
  );

  const derived = useMemo(() => {
    const empty = {
      photosByItem: new Map(), primaryByItem: new Map(), capturedItems: new Set(),
      locationsList: [], sharedLocations: new Set(),
      stats: { captured: 0, remaining: 0, locations: 0, uniqueLocations: 0, totalPhotos: 0 },
    };
    if (!activeHunt) return empty;

    const huntPhotos = state.photos.filter(p => p.huntId === activeHunt.id);

    // Group photos by item key, seeded with the hunt's full domain (dense
    // for Numbers/Alphabet, discovered-from-photos for Custom).
    const photosByItem = new Map();
    for (const key of domainForHunt(activeHunt, huntPhotos)) photosByItem.set(key, []);
    for (const p of huntPhotos) {
      const arr = photosByItem.get(p.itemKey) ?? [];
      arr.push(p);
      photosByItem.set(p.itemKey, arr);
    }

    // Primary photo per item (isPrimary flag, or first if none flagged)
    const primaryByItem = new Map();
    for (const [key, photos] of photosByItem) {
      primaryByItem.set(key, photos.find(p => p.isPrimary) ?? photos[0] ?? null);
    }

    // Captured items
    const capturedItems = new Set();
    for (const [key, photos] of photosByItem) {
      if (photos.length > 0) capturedItems.add(key);
    }

    // Unique location names
    const locSet = new Set(huntPhotos.map(p => p.location).filter(Boolean));
    const locationsList = [...locSet].sort((a, b) => a.localeCompare(b));

    // Shared locations: location is primary for 2+ different items
    const primaryLocationCount = new Map(); // location -> Set<itemKey>
    for (const [key, primary] of primaryByItem) {
      if (!primary) continue;
      const loc = primary.location;
      if (!loc) continue;
      if (!primaryLocationCount.has(loc)) primaryLocationCount.set(loc, new Set());
      primaryLocationCount.get(loc).add(key);
    }
    const sharedLocations = new Set();
    for (const [loc, keys] of primaryLocationCount) {
      if (keys.size >= 2) sharedLocations.add(loc);
    }

    const stats = {
      captured:        capturedItems.size,
      remaining:        photosByItem.size - capturedItems.size,
      locations:       locSet.size,
      uniqueLocations: locSet.size - sharedLocations.size,
      totalPhotos:     huntPhotos.length,
      total:           photosByItem.size,
    };

    return { photosByItem, primaryByItem, capturedItems, locationsList, sharedLocations, stats };
  }, [state.photos, activeHunt]);

  // ── Convenience actions ────────────────────────────────────────────────────

  const upsertPhotoState = useCallback(async (photo) => {
    await upsertPhoto(photo);
    dispatch({ type: 'UPSERT_PHOTO', photo });
  }, []);

  const removePhotoState = useCallback(async (id) => {
    await deletePhotoLocal(id);
    dispatch({ type: 'DELETE_PHOTO', id });
  }, []);

  const upsertHuntState = useCallback(async (hunt) => {
    await upsertHunt(hunt);
    dispatch({ type: 'UPSERT_HUNT', hunt });
  }, []);

  const value = {
    state,
    dispatch,
    derived,
    activeHunt,
    user,
    isOnline,
    signInWithGoogle,
    signOut,
    upsertPhotoState,
    removePhotoState,
    upsertHuntState,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function AppProvider({ children }) {
  return (
    <AuthProvider>
      <AppInner>{children}</AppInner>
    </AuthProvider>
  );
}

export function useAppContext() {
  return useContext(AppContext);
}
